import {Color} from "./colorObject";
import {ColorUtils} from "./colorUtils";
import {SelObTypes} from "./SelObTypes";

import {Z_OrderTable} from "./zOrderTable";
import {
    ChromoLogoConstructorArguments,
    EinfaerbbaresLogoConstructorArguments,
    StockLogoConstructorArguments
} from "./selObInterfaces.js";

import {TextObject, ChromoLogo, Logo, SelectableObject, StockLogo} from "./selectableObjects";

import {ColorCodeAliasTranslator} from "./controllerModules/colorcodeAliasHandler";
import {PalettenLoader} from "./palettenLoader";
import {FontHandler} from "./fontHandler";
import {intToBool} from "../global/globalUtils";
import {
    ColorCodeAbhaengigkeitsArten,
    ColorCodeFarbKombiBeziehung,
    ColorCodeFromDB,
    LogoObjectFromBackend
} from "./databaseInterfaceClasses";
import {Vector2} from "./delme_globals";
import {CONSTANTS} from "./meta_data";
import {ResourceInvalidationTriggers} from "../global/resourcemanager/resourceManager";
import {EinzelExemplar, ExemplarListenText} from "../global/einzelexemplare/einzelExemplarObject";
import globalsObject from "./globalsObject";
import {IEntwurfStoreWrapper} from "./EntwurfStateWrapper";
import {arrayIndexByKeyValue, getUniqueProperties} from "../global/arrayHelper";
import {ShaderCreator} from "./shader/OwayoShader";

export class EntwurfLoadingStati {
    colorObjects = false;
    textObjects = false;
    logoObjects = false;
    exemplareList = false;
}

export type EntwurfConstructorArguments = {
    id,
    productCategory,
    design,
    model,
    uploadedByOwayo?,
    basedOnDeprecatedProduct?,
    ersetztDurch?,
    ersetztDurchAuwiProdukt?: string
    name?,
    wasOrdered?,
    isInWarenkorb?,
    startDirty?: boolean
}

export class Entwurf {
    public static dataInteractor: IEntwurfStoreWrapper;

    public static setDataInteractor(interactor: IEntwurfStoreWrapper) {
        Entwurf.dataInteractor = interactor;
    }

    public static setData(args: EntwurfConstructorArguments) {

        const d = Entwurf.dataInteractor;
        d.setID(args.id);
        d.setDesignname(args.design);
        d.setModel(args.model);
        d.setProductCategory(args.productCategory);
        d.setColorObjects([]);
        d.setLogoObjects([]);
        d.setTextObjects([]);
        d.setPatterns([]);
        d.setZOrderTable(new Z_OrderTable());
        d.resetLoadStatus();
        d.setExemplarListe([]);
        d.setInBasket(parseInt(args.isInWarenkorb) === 1);
        d.setDesignanfrage(false);
        d.setChangedSinceSave(false);
        d.setOwayoLogosOnTop(true);
        d.setUploadedByOwayo(args.uploadedByOwayo || "0");
        d.setBasedOnDeprecatedProduct(args.basedOnDeprecatedProduct || false);
        d.setFarbvariante("");
        d.setName(args.name || "");
        d.setWasOrdered(args.wasOrdered || false);
        d.setFBPostID("");
        d.setRdyDesignID("");
        d.setHerstellerlogosDeleted(false);
        d.resetLoadStatus();
        if (args.startDirty) {
            d.SET_ENTWURF_changedSinceSave();
        }
    }


    private static getMasterColor(color: Color) {
        if (color.dependentOn === null) {
            return null;
        }
        let otherColor = color;

        while (
            otherColor !== null &&
            (otherColor.isDependent || otherColor.isHideable) &&
            otherColor.dependentOn !== null &&
            otherColor.dependentOn !== otherColor.colorCode
            ) {
            otherColor = Entwurf.getColorObjectByColorCode(otherColor.dependentOn);
        }
        return otherColor;
    }

    private static getMultipleMasterColorObjects(color: Color) {
        const masterColors = [] as Color[];
        for (const colorCode in color.percentages) {
            masterColors.push((Entwurf.dataInteractor.getColorObjects().find(c => c.colorCode === colorCode)) as Color);
        }
        return masterColors;
    }

    /**
     * Das Array colorObjects wird durchlaufen und Abhängigkeiten gesetzt. Ausserdem werden bei abhängigen Farben die Werte ihrer "Master" übernommen.
     * Ist eine Farbe abhänigi von einer abhängigen Farbe, so wir in der Verweis für das Update in die Farbe gesetzt, die nicht abhängig ist.
     * Bsp: OOF ->OOFK -> A   : in Farbe A.dependentColors enthält Link auf OOF
     */
    public static setDependencyAndDependentColorValues(farbBeziehungenZwischenColorCodes: ColorCodeFarbKombiBeziehung[]) {
        Entwurf.dataInteractor.getColorObjects().forEach(function (color) {
            if (color.isHideable) {
                color.setHideableDependencyTo(Entwurf.getMasterColor(color));
                return;
            }
            if (color.isDependent) {

                if (color.farbAbhaengigkeitsArt !== ColorCodeAbhaengigkeitsArten.HAS_COLORKOMBINATION_ACCORDING_TO_COLORCODES_REGELN) {
                    color.setDependencyTo(Entwurf.getMasterColor(color));
                } else {
                    const abhaengigkeit = farbBeziehungenZwischenColorCodes.find(r => r.folgeColorCode === color.colorCode);
                    if (abhaengigkeit !== undefined) {
                        color.dependentOn = abhaengigkeit.colorCode;
                    }

                }
            }

            if (color.multipleMasters) {
                color.setDependencyToMultipleMasters(Entwurf.getMultipleMasterColorObjects(color));
            }
        });
    }

    /**
     * Gibt an, ob der Entwurf gespeichert werden sollte. das ist der Fall wenn die ID noch eine UUID ist oder das Flag "changedSinceSave" gesetzt wurde.
     */
    public static needsSaving(): boolean {
        return Entwurf.dataInteractor.getChangedSinceSave() || Entwurf.isGuid(Entwurf.dataInteractor.getID());
    }

    private static isGuid(s: string) {
        return s.match(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/) !== null;
    }

    public static createColorObjectFromDBColorObject(dbColorObject, ColorCodeFarbVorbelegungen, ColorCodeAbhaengigkeiten): Color {
        const ccAbhaengigkeit = ColorCodeAbhaengigkeiten.find(c => c.ColorCode === dbColorObject.colorCode) || null;

        const colorWithMinDeviationFromPalette = ColorUtils.getColorWithMinDeviationFromPalette(
            ColorCodeFarbVorbelegungen[dbColorObject.colorCode],
            PalettenLoader.getFlattenedPalettenCluster(dbColorObject.PalettenCluster)
        );
        return new Color(
            {
                colorCode: dbColorObject.colorCode,
                colorCodeToDisplay: dbColorObject.colorCodeToDisplay || dbColorObject.colorCode,
                colorValue: colorWithMinDeviationFromPalette,
                palette: dbColorObject.Palette,
                palettenCluster: dbColorObject.PalettenCluster,
                isVisible: true,
                colorCodeNr: parseInt(dbColorObject.ColorCodeNr, 10),
                farbname: dbColorObject.Farbname === null ? dbColorObject.ColorCode : dbColorObject.Farbname,
                blackWhiteOnly: dbColorObject.NurSchwarzWeiss,
                isDependent: dbColorObject.IsLinkedColorCodeImWeb,
                dependentOn: ccAbhaengigkeit === null ? null : ccAbhaengigkeit.MasterColorcode,
                groupID: parseInt(dbColorObject.GroupID, 10),
                inKonfi: dbColorObject.InKonfi,
                isHideable: dbColorObject.IsHideable,
                blendToBackground: dbColorObject.blendToBackground,
                multipleMasters: ccAbhaengigkeit === null ? null : ccAbhaengigkeit.VerteilungUeberPercentages,
                percentages: ccAbhaengigkeit === null ? null : typeof (ccAbhaengigkeit.Percentages) === "undefined" ? {} : ccAbhaengigkeit.Percentages,
                farbAbhaengigkeitsArt: dbColorObject.farbAbhaengigkeitsArt
            }
        );
    }

    /**
     * Beim Designwechsel werden die ColorCodes neu geladen (wegen Abhängigkeit, evtl anderen ColorCodes etc)
     * Visibititly der Farben wird hier auf true gesetzt, das endgültige sichtbar schalten erfolgt in setColorVisibleStatus
     */
    public static updateColorCodes(colorObjectArray: ColorCodeFromDB[], ColorCodeFarbVorbelegungen, ColorCodeAbhaengigkeiten, farbBeziehungenZwischenColorCodes: ColorCodeFarbKombiBeziehung[]) {


        const colorObjectArrayWithAliases = ColorCodeAliasTranslator.setAliases(colorObjectArray, Entwurf.dataInteractor.getDesign());
        colorObjectArrayWithAliases.forEach(function (color) {
            const newColor = Entwurf.createColorObjectFromDBColorObject(color, ColorCodeFarbVorbelegungen, ColorCodeAbhaengigkeiten);
            const index = arrayIndexByKeyValue(Entwurf.dataInteractor.getColorObjects(), "colorCode", color.colorCode);
            if (index > -1) {
                const oldColor = Entwurf.dataInteractor.getColorObjects()[index];
                newColor.colorValue = oldColor.colorValue;
                newColor.palette = oldColor.palette;
                Entwurf.dataInteractor.replaceColorObject(index, newColor);
            } else {
                Entwurf.dataInteractor.addColor(newColor);
            }
        });
        Entwurf.dataInteractor.setDependencyAndDependentColorValues(farbBeziehungenZwischenColorCodes);
    }

    public static getSelectableObject(id: string): SelectableObject | null {
        const d = Entwurf.dataInteractor;
        return ([d.getLogoObjects(), d.getTextObjects(), d.getPatterns()] as SelectableObject[][]).reduce(
            function (carry: SelectableObject | null, arr: SelectableObject[]) {
                if (carry !== null) {
                    return carry;
                }
                return arr.find(obj => obj.id === id) || null;
            },
            null
        );
    }

    public static getSelOb(id): SelectableObject | null {
        return this.getSelectableObject(id);
    }

    public static deleteSelObById(id) {
        let index;
        const d = Entwurf.dataInteractor;
        [d.getLogoObjects(), d.getTextObjects()].forEach(function (array) {
                index = array.findIndex(obj => obj.id === id);
                if (index > -1) {
                    array.splice(index, 1);
                }
            }
        )
    }

    private static createTextObjectFromDBTextObjectWithoutRelSizeAndTranslation(dbTextObject): TextObject {
        let newText = new TextObject({
            id: dbTextObject.name,
            partName: dbTextObject.part,
            font: dbTextObject.font,
            value: dbTextObject.value,
            colorValue: dbTextObject.color,
            outlineColorValue: dbTextObject.outlineColor,
            outlineWidth: parseFloat(dbTextObject.outlineWidth) * TextObject.getFontSize(dbTextObject.value),
            outlineLineJoin: FontHandler.getOutlineLineJoin(dbTextObject.font),
            isFixed: intToBool(parseInt(dbTextObject.fix, 10)),
            floating: parseInt(dbTextObject.floating, 10),
            keepSeparate: intToBool(parseInt(dbTextObject.keepSeparate, 10)),
            allowDelete: intToBool(parseInt(dbTextObject.allowDelete, 10)),
            allowResize: intToBool(parseInt(dbTextObject.allowResize, 10)),
            allowDrag: intToBool(parseInt(dbTextObject.allowDrag, 10)),
            isVorbelegung: intToBool(parseInt(dbTextObject.isVorbelegung, 10)),
            outlineZweiColor: dbTextObject.outlineZweiColor,
            outlineZweiColorPalette: dbTextObject.outlineZweiColorPalette,
            characterSpacingProzent: dbTextObject.characterSpacingProzent,
            ursprungsMotivID: parseInt(dbTextObject.ursprungsMotivID),
            hasTackleTwill: dbTextObject.hasTackleTwill !== undefined ? dbTextObject.hasTackleTwill : false,
            berechnungsFeature: dbTextObject.berechnungsFeature !== undefined ? dbTextObject.berechnungsFeature : null
        });
        newText.rotation = parseFloat(dbTextObject.rotation);
        newText.isPinned = intToBool(parseInt(dbTextObject.isPinned, 10));
        return newText;

    }

    private static createTextObjectFromDBTextObject(dbTextObject): TextObject {
        const newText = Entwurf.createTextObjectFromDBTextObjectWithoutRelSizeAndTranslation(dbTextObject);
        newText.DBRotatedRelSize = new Vector2(parseFloat(dbTextObject.relSize.x), parseFloat(dbTextObject.relSize.y));
        newText.translation.x = parseFloat(dbTextObject.point.x);
        newText.translation.y = parseFloat(dbTextObject.point.y);
        return newText;
    }

    public static loadTextObjects(textsFromDB): void {
        textsFromDB.forEach(
            (text) => {
                const newText = Entwurf.createTextObjectFromDBTextObject(text);
                Entwurf.dataInteractor.addTextObject(newText);
                Entwurf.dataInteractor.getZOrderTable().insertEntryIntoZIndex(newText.id, CONSTANTS.FLOAT_FREE, parseInt(text.zIndex, 10));
            }
        );
        Entwurf.dataInteractor.setLoadStatusToTrue("TEXT");
    }


    // private static parseDateFromDBString(dateString): { year, month, day, hour, minutes, seconds } {
    //     const regex = /(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/;
    //     const parsed = regex.exec(dateString) as string[];
    //
    //     return {
    //         year: parsed[1],
    //         month: parsed[2],
    //         day: parsed[3],
    //         hour: parsed[4],
    //         minutes: parsed[5],
    //         seconds: parsed[6]
    //     };
    // }


    private static createStockLogoFromDBLogoObject(dbLogoObject): StockLogo {
        const parameters = this.getEinfaerbbaresLogoParameters(dbLogoObject, dbLogoObject.stockLogoID) as StockLogoConstructorArguments;
        parameters.stockLogoID = dbLogoObject.stockLogoID;

        const newLogo = new StockLogo(parameters);
        newLogo.getOriginalSVG(false);
        getUniqueProperties(dbLogoObject.colors, c => c.Palette).forEach(p => PalettenLoader.loadSinglePalette(p));
        return newLogo;
    }

    private static createChromoLogoFromDBLogoObject(dbLogoObject, filename): ChromoLogo {
        const parameters = this.getEinfaerbbaresLogoParameters(dbLogoObject, filename);
        parameters.chromoLogoId = dbLogoObject.chromoLogoId;
        const newLogo = new ChromoLogo(parameters as ChromoLogoConstructorArguments);
        newLogo.getOriginalSVG(false);
        getUniqueProperties(dbLogoObject.colors, c => c.Palette).forEach(p => PalettenLoader.loadSinglePalette(p));
        return newLogo;
    }

    private static getEinfaerbbaresLogoParameters(dbLogoObject, filename: string): EinfaerbbaresLogoConstructorArguments {
        return {
            id: dbLogoObject.name,
            partName: dbLogoObject.part,
            filename: filename,
            pathToLogo: dbLogoObject.pathToLogo,
            chromoLogoId: null,
            logoId: null,
            stockLogoID: null,
            palette: dbLogoObject.Palette,
            floating: parseInt(dbLogoObject.floating, 10),
            keepSeparate: intToBool(parseInt(dbLogoObject.keepSeparate, 10)),
            allowDelete: intToBool(parseInt(dbLogoObject.allowDelete, 10)),
            allowResize: intToBool(parseInt(dbLogoObject.allowResize, 10)),
            allowDrag: intToBool(parseInt(dbLogoObject.allowDrag, 10)),
            allowColorChange: intToBool(parseInt(dbLogoObject.allowColorChange, 10)),
            isVorbelegung: intToBool(parseInt(dbLogoObject.isVorbelegung, 10)),
            colors: dbLogoObject.colors,
            isAILogo: false,
            ursprungsMotivID: parseInt(dbLogoObject.ursprungsMotivID),
            hasTackleTwill: dbLogoObject.hasTackleTwill !== undefined ? dbLogoObject.hasTackleTwill : false,
            berechnungsFeature: dbLogoObject.berechnungsFeature !== undefined ? dbLogoObject.berechnungsFeature : null,
            zuLoeschenBeiProduktUndDesignWechsel: dbLogoObject.zuLoeschenBeiProduktUndDesignWechsel || 0
        };
    }

    private static createLogoObjectFromDBLogoObject(dbLogoObject, filename): Logo {
        return new Logo({
            id: dbLogoObject.name,
            partName: dbLogoObject.part,
            filename: filename,
            pathToLogo: dbLogoObject.pathToLogo,
            logoId: dbLogoObject.logoId,
            floating: parseInt(dbLogoObject.floating, 10),
            keepSeparate: intToBool(parseInt(dbLogoObject.keepSeparate, 10)),
            allowDelete: intToBool(parseInt(dbLogoObject.allowDelete, 10)),
            allowResize: intToBool(parseInt(dbLogoObject.allowResize, 10)),
            allowDrag: intToBool(parseInt(dbLogoObject.allowDrag, 10)),
            isVorbelegung: intToBool(parseInt(dbLogoObject.isVorbelegung, 10)),
            isAILogo: dbLogoObject.isAILogo,
            ursprungsMotivID: parseInt(dbLogoObject.ursprungsMotivID),
            hasTackleTwill: dbLogoObject.hasTackleTwill !== undefined ? dbLogoObject.hasTackleTwill : false,
            berechnungsFeature: dbLogoObject.berechnungsFeature !== undefined ? dbLogoObject.berechnungsFeature : null,
            zuLoeschenBeiProduktUndDesignWechsel: dbLogoObject.zuLoeschenBeiProduktUndDesignWechsel || 0
        });
    }

    private static inferLogoType(logo): string {
        return logo.logoId !== null ? SelObTypes.LOGO :
            logo.chromoLogoId !== null ? SelObTypes.CHROMOLOGO :
                SelObTypes.STOCKLOGO;
    }

    private static fillInCommonAttributes(selOb: TextObject | Logo | ChromoLogo | StockLogo, dbObject): TextObject | Logo | ChromoLogo | StockLogo {
        selOb.translation.x = parseFloat(dbObject.point.x);
        selOb.translation.y = parseFloat(dbObject.point.y);
        selOb.rotation = parseFloat(dbObject.rotation);
        selOb.isPinned = intToBool(parseInt(dbObject.isPinned, 10));
        return selOb;
    }

    private static getFileNameFromPathString(pathString): string {
        return pathString.substring(pathString.lastIndexOf("/") + 1);
    }

    private static createNewLogoObject(logo): Logo | StockLogo | ChromoLogo {
        const logoPoolId = Entwurf.inferLogoType(logo) === SelObTypes.STOCKLOGO ? "" : Entwurf.getFileNameFromPathString(logo.pathToLogo);
        switch (Entwurf.inferLogoType(logo)) {

            case SelObTypes.CHROMOLOGO:
                return Entwurf.createChromoLogoFromDBLogoObject(logo, logoPoolId);
            case SelObTypes.STOCKLOGO:
                return Entwurf.createStockLogoFromDBLogoObject(logo);
            case SelObTypes.LOGO:
            default:

                return Entwurf.createLogoObjectFromDBLogoObject(logo, logoPoolId);
        }
    }

    public static loadLogoObjects(logosFromDB: LogoObjectFromBackend[]) {
        logosFromDB.forEach(function (logo) {
            let newLogo = Entwurf.fillInCommonAttributes(Entwurf.createNewLogoObject(logo), logo) as StockLogo | Logo | ChromoLogo;
            newLogo.relSize.x = parseFloat(logo.relSize.x.toString());
            newLogo.relSize.y = parseFloat(logo.relSize.y.toString());
            newLogo = Entwurf.fillCommonLogoProperties(newLogo, logo);
            Entwurf.dataInteractor.addLogo(newLogo);
            Entwurf.dataInteractor.getZOrderTable().insertEntryIntoZIndex(newLogo.id, newLogo.floating, parseInt(logo.zIndex, 10));
        });
        Entwurf.dataInteractor.setLoadStatusToTrue("LOGO");
    }

    private static fillCommonLogoProperties(newLogo: StockLogo | Logo | ChromoLogo, logoFromDB: LogoObjectFromBackend) {
        newLogo.repeat = intToBool(parseInt(logoFromDB.repeat, 10));
        newLogo.clampToColor = parseInt(logoFromDB.clampToColor || "-1", 10);
        newLogo.wasserzeichen = intToBool(parseInt(logoFromDB.wasserzeichen, 10));
        newLogo.wasserzeichenInvertImage = intToBool(parseInt(logoFromDB.wasserzeichenInvertImage, 10));
        newLogo.wasserzeichenIntensity = parseFloat(logoFromDB.wasserzeichenIntensity);
        return newLogo
    }

    private static checkSingleColorForDepedencySuspension(color: Color) {
        let masterColor, parentColor;
        const palettenCluster = PalettenLoader.getFlattenedPalettenCluster(color.palettenCluster);
        if (typeof (palettenCluster) === "undefined" || palettenCluster === null || Object.keys(palettenCluster).length === 0) {
            globalsObject.resourceManager.createTask("checkColor" + color.colorCode + "Dependency")
                .invalidAfter(ResourceInvalidationTriggers.PRODUCTCHANGE)
                .when("palettenCluster_" + color.palettenCluster, "loaded")
                .thenDo(function () {
                    Entwurf.checkSingleColorForDepedencySuspension(color);
                });
            return;
        }

        if (color.isDependent && color.dependentOn !== null) {
            masterColor = Entwurf.getMasterColor(color);
            parentColor = Entwurf.getColorObjectByColorCode(color.dependentOn);

            if (!color.valueMatchesMasterColor(masterColor)) {
                Entwurf.dataInteractor.setOwayoLogosOnTop(false);
                color.severDependencyFrom(parentColor);
                masterColor.colorNoLongerDependent(color.colorCode);
            }
            return;
        }
        if (color.isHideable) {
            masterColor = Entwurf.getMasterColor(color);
            color.setBlendToBackgroundFlagFromMasterColor(masterColor);
            return;
        }
        if (color.multipleMasters) {
            if (color.getColorByPercentages(palettenCluster) !== color.getValueAsHex()) {
                color.severDependencyFromMultipleMasters();
            }
        }
    }

    public static loadColorValues(colorsFromDB) {
        colorsFromDB.forEach(fromDB => {
            const toChange = Entwurf.dataInteractor.getColorObjects().filter(c => c.colorCode === fromDB.bezeichnung || c.colorCodeToDisplay === fromDB.bezeichnung);
            toChange.forEach(c => c.setDecimalValue(parseInt(fromDB.colorRGB, 10), fromDB.palette, true))
        });
        Entwurf.dataInteractor.getColorObjects().forEach(co => Entwurf.checkSingleColorForDepedencySuspension(co));
        Entwurf.dataInteractor.setLoadStatusToTrue("COLOR");
    }

    private static createEinzelExemplarTextArray(einzelexemplar) {
        return einzelexemplar.texts.map(t => {
            return new ExemplarListenText(
                t.isFixed,
                t.value,
                t.textObjectUUID,
                t.masterID
            );
        })
    }

    static loadEinzelexemplare(exemplareFromDB) {
        for (let i = 0; i < exemplareFromDB.length; i = i + 1) {
            const einzelexemplar = exemplareFromDB[i];

            const texts = Entwurf.createEinzelExemplarTextArray(einzelexemplar);

            const newEinzelexemplar = new EinzelExemplar(einzelexemplar.size, einzelexemplar.amount, texts);
            Entwurf.dataInteractor.addExemplar(newEinzelexemplar);
        }
        Entwurf.dataInteractor.setLoadStatusToTrue("EXEMPLARLISTE");
    }

    static unsetCloneFlags() {
        const d = Entwurf.dataInteractor;
        [d.getLogoObjects(), d.getTextObjects()].forEach(a => Entwurf.unsetCloneFlagsInArrray(a as SelectableObject[]));
    }

    private static unsetCloneFlagsInArrray(arrayToUnsetFlagsIn: SelectableObject[]) {
        arrayToUnsetFlagsIn.forEach((selOb: SelectableObject) => selOb.isClone = false);
    }

    /**
     * Voight-Kampff Test für Objekte. Wer zuerst kommt hat Glück und wird als Original anerkannt, alle Objekte in dessen cloneIds Array werden als Klon markiert
     * Neuer Algorithmus. Wir wollen Objekte, deren UV Koordinaten ausserhalb von [0,1] liegen als Klone ansehen, falls es ein Original gibt...
     */
    static setCloneFlags() {
        Entwurf.unsetCloneFlags();
        const d = Entwurf.dataInteractor;
        [d.getLogoObjects(), d.getTextObjects()].forEach(a => this.setCloneFlagsInArray(a as SelectableObject[]));
    }

    private static setCloneFlagsInArray(arrayToSetFlagsIn: SelectableObject[]) {
        arrayToSetFlagsIn.forEach((selOb) => {
            if (selOb.isClone === false && selOb.translationIsInBounds()) {
                Entwurf.setCloneFlagsOnClonesOfObject(selOb);
            }
        })
    }

    private static setCloneFlagsOnClonesOfObject(selObInArray: SelectableObject) {
        selObInArray.cloneIds.forEach(cloneID => (this.getSelOb(cloneID) as SelectableObject).isClone = true);
    }

    public static getColorObjectByColorCode(colorCode) {
        return Entwurf.dataInteractor.getColorObjects().find(c => c.colorCode === colorCode) || null;
    }

    public static getColorCodeFromColorCodeNr(colorCodeNr: number) {
        const o = Entwurf.dataInteractor.getColorObjects().find(c => c.colorCodeNr === colorCodeNr) || null;
        if (o !== null) {
            return o.colorCode;
        }
        return "";
    }
}

ShaderCreator.setOwayoLogosOnTopGetter(() => Entwurf.dataInteractor.getOwayoLogosOnTop());
